<script setup>
import { ref, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const emit = defineEmits(['submit']);

const { t } = useI18n();

const AVAILABILITY = [
  'in stock',
  'out of stock',
  'preorder',
  'available for order',
];
const selectClass =
  'w-full h-10 px-3 text-sm rounded-xl outline-1 outline outline-n-weak bg-n-background text-n-slate-12';

const dialogRef = ref(null);
const editingId = ref(null);
const errors = reactive({ name: '', retailer_id: '' });

const form = reactive({
  retailer_id: '',
  name: '',
  description: '',
  price: '',
  currency: 'BRL',
  availability: 'in stock',
  image_url: '',
  url: '',
});

const resetForm = (product = {}) => {
  form.retailer_id = product.retailer_id || '';
  form.name = product.name || '';
  form.description = product.description || '';
  form.price = product.price || '';
  form.currency = product.currency || 'BRL';
  form.availability = product.availability || 'in stock';
  form.image_url = product.image_url || '';
  form.url = product.url || '';
  errors.name = '';
  errors.retailer_id = '';
};

const open = (product = null) => {
  editingId.value = product?.id || null;
  resetForm(product || {});
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

defineExpose({ open, close });

const validate = () => {
  errors.name = form.name.trim()
    ? ''
    : t('WHATSAPP_SETTINGS.CATALOG.FORM.NAME_REQUIRED');
  errors.retailer_id = form.retailer_id.trim()
    ? ''
    : t('WHATSAPP_SETTINGS.CATALOG.FORM.RETAILER_ID_REQUIRED');
  return !errors.name && !errors.retailer_id;
};

const onConfirm = () => {
  if (!validate()) return;
  emit('submit', { id: editingId.value, product: { ...form } });
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="
      editingId
        ? t('WHATSAPP_SETTINGS.CATALOG.FORM.EDIT_TITLE')
        : t('WHATSAPP_SETTINGS.CATALOG.FORM.CREATE_TITLE')
    "
    :confirm-button-label="t('WHATSAPP_SETTINGS.CATALOG.FORM.SUBMIT')"
    width="xl"
    @confirm="onConfirm"
  >
    <div class="flex flex-col gap-4">
      <Input
        v-model="form.retailer_id"
        :label="t('WHATSAPP_SETTINGS.CATALOG.FORM.RETAILER_ID')"
        :disabled="!!editingId"
        :message="errors.retailer_id"
        :message-type="errors.retailer_id ? 'error' : 'info'"
      />
      <Input
        v-model="form.name"
        :label="t('WHATSAPP_SETTINGS.CATALOG.FORM.NAME')"
        :message="errors.name"
        :message-type="errors.name ? 'error' : 'info'"
      />
      <Input
        v-model="form.description"
        :label="t('WHATSAPP_SETTINGS.CATALOG.FORM.DESCRIPTION')"
      />
      <div class="grid grid-cols-2 gap-4">
        <Input
          v-model="form.price"
          :label="t('WHATSAPP_SETTINGS.CATALOG.FORM.PRICE')"
          placeholder="10000"
        />
        <Input
          v-model="form.currency"
          :label="t('WHATSAPP_SETTINGS.CATALOG.FORM.CURRENCY')"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('WHATSAPP_SETTINGS.CATALOG.FORM.AVAILABILITY') }}
        </label>
        <select v-model="form.availability" :class="selectClass">
          <option v-for="option in AVAILABILITY" :key="option" :value="option">
            {{ option }}
          </option>
        </select>
      </div>
      <Input
        v-model="form.image_url"
        :label="t('WHATSAPP_SETTINGS.CATALOG.FORM.IMAGE_URL')"
        placeholder="https://..."
      />
      <Input
        v-model="form.url"
        :label="t('WHATSAPP_SETTINGS.CATALOG.FORM.URL')"
        placeholder="https://..."
      />
    </div>
  </Dialog>
</template>
